import { Button, Dialog, IconButton, Switch } from '@mui/material';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupSelectMultiple,
  FilterRow,
  SubjectSelectMultiple,
  toggleMultipleValueArrayProperty,
} from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { CrossIcon, ModalContent } from '@schooly/style';
import React, { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ModalFooter } from '../../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../../components/uikit-components/Modal/ModalHeader';
import {
  emptyRegisterGroupsFilters,
  RegisterGroupsFilterOptions,
  RegisterGroupsFilters,
} from './utils';

import { Stack } from '@mui/material';
import { PropertyTypeTagSelect, SubjectTagSelect } from '@schooly/components/filters';
import { TagSelect, TruncatedTagSelect } from '@schooly/style';

type RegisterGroupsFilterDialogProps = {
  filters: RegisterGroupsFilters;
  options: RegisterGroupsFilterOptions;
  onClose: () => void;
  onApply: (filters: RegisterGroupsFilters) => void;
};

export const RegisterGroupsFilterDialog: FC<RegisterGroupsFilterDialogProps> = ({
  filters,
  options,
  onClose,
  onApply,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();
  const [draft, setDraft] = useState<RegisterGroupsFilters>(filters);

  const showSubjectRow = !!options.availableSubjectIds.length;
  const showTutorRow = options.showTutorOnly;

  const clearAndApply = () => {
    const cleared = emptyRegisterGroupsFilters();
    setDraft(cleared);
    onApply(cleared);
    onClose();
  };

  return (
    <Dialog
      fullWidth
      open
      onClose={onClose}
      sx={(theme) => ({
        zIndex: theme.zIndex.modal + 1,
        '& .MuiDialog-paperFullWidth': {
          width: 600,
          height: 'unset',
        },
      })}
    >
      <ModalHeader title={formatMessage({ id: 'groups-Filter-Title' })} active>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent active>
        <FilterRow label={formatMessage({ id: 'schoolProperty-AgeGroup' })}>
          <AgeGroupSelectMultiple
            borderOnHover
            allowArchived
            schoolId={schoolId}
            onClear={() => setDraft((prev) => ({ ...prev, ageGroupIds: [] }))}
            optionsTitle={formatMessage({ id: 'schoolProperty-AgeGroup-plural' })}
            userRole={SchoolUserRole.Student}
            selectedIds={draft.ageGroupIds}
            onSelect={(ids) =>
              setDraft((prev) => ({
                ...prev,
                ageGroupIds: toggleMultipleValueArrayProperty(prev.ageGroupIds, ids),
              }))
            }
          />
        </FilterRow>

        {showSubjectRow && (
          <FilterRow label={formatMessage({ id: 'section-Subjects' })}>
            <SubjectSelectMultiple
              borderOnHover
              allowArchived
              schoolId={schoolId}
              availableIds={options.availableSubjectIds}
              selectedIds={draft.subjectIds}
              disabled={draft.onlyTutorGroups}
              placeholder={
                draft.onlyTutorGroups
                  ? formatMessage({ id: 'groups-GroupSubject-disabledFilter-Hint' })
                  : undefined
              }
              onClear={() => setDraft((prev) => ({ ...prev, subjectIds: [] }))}
              onSelectSubjectId={(id) =>
                setDraft((prev) => ({
                  ...prev,
                  onlyTutorGroups: false,
                  subjectIds: toggleMultipleValueArrayProperty(prev.subjectIds, [id]),
                }))
              }
            />
          </FilterRow>
        )}

        {showTutorRow && (
          <FilterRow label={formatMessage({ id: 'groups-OnlyTutorGroups' })}>
            <Switch
              sx={{ ml: 1.25 }}
              checked={draft.onlyTutorGroups}
              onChange={(_, checked) =>
                setDraft((prev) => ({
                  ...prev,
                  onlyTutorGroups: checked,
                  subjectIds: checked ? [] : prev.subjectIds,
                }))
              }
            />
          </FilterRow>
        )}
      </ModalContent>
      <ModalFooter active>
        <Button variant="outlined" onClick={clearAndApply}>
          {formatMessage({ id: 'filter-Clear' })}
        </Button>
        <Button
          onClick={() => {
            onApply(draft);
            onClose();
          }}
        >
          {formatMessage({ id: 'filter-Apply' })}
        </Button>
      </ModalFooter>
    </Dialog>
  );
};

type RegisterGroupsInlineFilterTagsProps = {
  filters: RegisterGroupsFilters;
  onOpenFilters?: () => void;
};

export const RegisterGroupsInlineFilterTags: FC<RegisterGroupsInlineFilterTagsProps> = ({
  filters,
  onOpenFilters,
}) => {
  const { schoolId } = useAuth();
  const { formatMessage } = useIntl();

  const tagItems = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (!schoolId) return items;

    const commonTagsProps = {
      size: 'small',
      ...(onOpenFilters && { onClick: onOpenFilters }),
      sx: { maxWidth: 115 },
    } as const;

    if (filters.onlyTutorGroups) {
      items.push(
        <TagSelect
          key="tutor"
          label={formatMessage({ id: 'groups-OnlyTutorGroups' })}
          {...commonTagsProps}
        />,
      );
    }

    for (const id of filters.ageGroupIds) {
      items.push(
        <PropertyTypeTagSelect
          key={id}
          id={id}
          schoolId={schoolId}
          userRole={SchoolUserRole.Student}
          {...commonTagsProps}
        />,
      );
    }

    for (const id of filters.subjectIds) {
      items.push(<SubjectTagSelect key={id} id={id} schoolId={schoolId} {...commonTagsProps} />);
    }

    return items;
  }, [
    schoolId,
    onOpenFilters,
    filters.onlyTutorGroups,
    filters.ageGroupIds,
    filters.subjectIds,
    formatMessage,
  ]);

  if (!tagItems.length) return null;

  return (
    <Stack flexDirection="row" alignItems="center" gap={1}>
      <TruncatedTagSelect
        spacing={2}
        items={tagItems}
        tooltipProps={{
          componentsProps: { tooltip: { sx: { maxWidth: 'unset' } } },
        }}
      />
    </Stack>
  );
};
